import ErrorHandler from "../error-handlers/axios/defaultErrorHandler";

/**
 * @description Fetches all blog keywords
 * @param {import("axios").AxiosInstance} axiosInstance - The axios instance for making the HTTP request
 * @param {import("axios").AxiosRequestConfig} [options] - Optional axios request config
 * @returns {Promise<Array>} List of blog keywords
 */
export const fetchBlogKeywords = async (axiosInstance, options) => {
  try {
    const res = await axiosInstance.get("/blog-keywords", options);
    return res?.data;
  } catch (error) {
    ErrorHandler.handle(error);
  }
};

/**
 * @description Fetches a specific blog keyword by ID
 * @param {import("axios").AxiosInstance} axiosInstance - The axios instance for making the HTTP request
 * @param {string} id - The ID of the blog keyword to fetch
 * @param {import("axios").AxiosRequestConfig} [options] - Optional axios request config
 * @returns {Promise<Object>} Blog keyword object
 */
export const fetchBlogKeywordById = async (axiosInstance, id, options) => {
  try {
    const res = await axiosInstance.get(`/blog-keywords/${id}`, options);
    return res?.data;
  } catch (error) {
    ErrorHandler.handle(error);
  }
};

/**
 * @description Creates a new blog keyword
 * @param {import("axios").AxiosInstance} axiosInstance - The axios instance for making the HTTP request
 * @param {Object} payload - The blog keyword data to create
 * @param {import("axios").AxiosRequestConfig} [options] - Optional axios request config
 * @returns {Promise<Object>} Created blog keyword
 */
export const createBlogKeyword = async (axiosInstance, payload, options) => {
  try {
    const res = await axiosInstance.post("/blog-keywords", payload, options);
    return res?.data;
  } catch (error) {
    ErrorHandler.handle(error);
  }
};

/**
 * @description Creates multiple blog keywords in bulk
 * @param {import("axios").AxiosInstance} axiosInstance - The axios instance for making the HTTP request
 * @param {Array<Object>} payload - Array of blog keyword data to create
 * @param {import("axios").AxiosRequestConfig} [options] - Optional axios request config
 * @returns {Promise<Array>} Created blog keywords
 */
export const createBulkBlogKeywords = async (axiosInstance, payload, options) => {
  try {
    const res = await axiosInstance.post("/blog-keywords/bulk", payload, options);
    return res?.data;
  } catch (error) {
    ErrorHandler.handle(error);
  }
};

/**
 * @description Updates a blog keyword
 * @param {import("axios").AxiosInstance} axiosInstance - The axios instance for making the HTTP request
 * @param {string} id - The ID of the blog keyword to update
 * @param {Object} payload - The updated blog keyword data
 * @param {import("axios").AxiosRequestConfig} [options] - Optional axios request config
 * @returns {Promise<Object>} Updated blog keyword
 */
export const updateBlogKeyword = async (axiosInstance, id, payload, options) => {
  try {
    const res = await axiosInstance.patch(`/blog-keywords/${id}`, payload, options);
    return res?.data;
  } catch (error) {
    ErrorHandler.handle(error);
  }
};

/**
 * @description Deletes a blog keyword
 * @param {import("axios").AxiosInstance} axiosInstance - The axios instance for making the HTTP request
 * @param {string} id - The ID of the blog keyword to delete
 * @param {import("axios").AxiosRequestConfig} [options] - Optional axios request config
 * @returns {Promise<Object>} Deletion response
 */
export const deleteBlogKeyword = async (axiosInstance, id, options) => {
  try {
    const res = await axiosInstance.delete(`/blog-keywords/${id}`, options);
    return res?.data;
  } catch (error) {
    ErrorHandler.handle(error);
  }
};
